// Tipos base para la aplicaciÃ³n InboMarket

// Re-exportar tipos de currency desde la configuración centralizada
export type { Currency, CurrencyCode, CurrencyInfo, CurrencyOption } from '@/shared/config/config';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  name?: string; // Nombre completo opcional (para compatibilidad)
  role: UserRole;
  tenantId: string;
  tenant?: string;
  avatar?: string;
  phone?: string;
  isActive: boolean;
  onboardingCompleted?: boolean;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
  profile?: {
    company?: string;
    [key: string]: any;
  };
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  domain?: string;
  logo?: string;
  primaryColor?: string;
  isActive: boolean;
  /**
   * @deprecated Usar subscription.plan en su lugar
   * Plan obtenido directamente (legacy)
   */
  plan?: Plan;
  modules: string[];
  settings: TenantSettings;
  createdAt: string;
  updatedAt: string;
  // Subscription Policy fields
  subscriptionPolicyId?: string;
  trialDays?: number;
  trialEnabled?: boolean;
  trialEndsAt?: string;
  subscriptionEndsAt?: string;
  expiresAt?: string;
  trialStartAt?: string;
  /**
   * Suscripción activa del cliente (tenant)
   * La relación correcta es: Tenant → Subscription → Plan
   */
  subscription?: TenantSubscription;
}

/**
 * Suscripción activa del cliente (tenant)
 */
export interface TenantSubscription {
  id: string;
  planId: string;
  plan: Plan;
  status: 'active' | 'trial' | 'expired' | 'cancelled' | 'pending' | 'suspended';
  startDate: string;
  endDate: string;
  trialEndsAt?: string;
  isInTrial?: boolean;
}

export interface Plan {
  id: string;
  name: string;
  price: number;
  currency: string;
  features: string[];
  maxUsers: number;
  maxProperties: number;
  maxStorage: number;
  maxLeads?: number;
  maxCampaigns?: number;
  trialDays?: number;
}

export interface TenantSettings {
  language: string;
  timezone: string;
  currency: string;
  dateFormat: string;
  enableNotifications: boolean;
  enableAnalytics: boolean;
}

export interface Property {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  propertyType: PropertyType;
  businessCategory: BusinessCategory;
  location: Location;
  characteristics: PropertyCharacteristics;
  media: Media[];
  status: PropertyStatus;
  tags: string[];
  tenantId: string;
  agentId?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Location {
  address: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  neighborhood?: string;
  landmarks?: string[];
}

export interface PropertyCharacteristics {
  area: number;
  areaUnit: string;
  bedrooms: number;
  bathrooms: number;
  parkingSpaces: number;
  floors: number;
  yearBuilt?: number;
  condition: 'new' | 'used' | 'renovated' | 'under_construction';
  amenities: string[];
}

export interface Media {
  id: string;
  url: string;
  type: 'image' | 'video' | 'document';
  filename: string;
  size: number;
  order: number;
  isPrimary: boolean;
}

export interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  source: LeadSource;
  stage: LeadStage;
  budget?: {
    min: number;
    max: number;
    currency: string;
  };
  preferences: LeadPreferences;
  assignedAgentId?: string;
  tenantId: string;
  interactions: Interaction[];
  createdAt: Date;
  updatedAt: Date;
}

export interface LeadSource {
  type: 'website' | 'social_media' | 'referral' | 'advertising' | 'other';
  name: string;
  campaign?: string;
}

export interface LeadStage {
  stage: 'new' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';
  probability: number;
  notes?: string;
}

export interface LeadPreferences {
  propertyTypes: PropertyType[];
  locations: string[];
  budget: {
    min: number;
    max: number;
    currency: string;
  };
  urgency: 'low' | 'medium' | 'high';
}

export interface Interaction {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'note' | 'other';
  content: string;
  agentId: string;
  timestamp: Date;
  nextAction?: string;
  nextActionDate?: Date;
}

// Enums
export enum UserRole {
  // Roles Globales (CORE)
  SUPER_ADMIN = 'SUPER_ADMIN',

  // Roles de Negocio (BUSINESS) - Solo los que terminan en _ADMIN
  AGENCY_ADMIN = 'AGENCY_ADMIN',
  BROKER_MANAGER_ADMIN = 'BROKER_MANAGER_ADMIN',
  PROJECT_DEVELOPER_ADMIN = 'PROJECT_DEVELOPER_ADMIN',
  AGENCY_AGENT_USER = 'AGENCY_AGENT_USER',

  // Roles de Soporte (SUPPORT)
  ACCOUNTING_TEAM = 'ACCOUNTING_TEAM',
  OPERATIONAL_TEAM = 'OPERATIONAL_TEAM',
  CONDOMINIUM_ADMIN = 'CONDOMINIUM_ADMIN',

  // Roles Especializados (SPECIALIZED)
  LEGAL_MODULE = 'LEGAL_MODULE',

  // Roles de Cliente (CLIENT)
  BUYER_CLIENT = 'BUYER_CLIENT',

  // Roles de Estado (STATUS)
  PENDING_ONBOARDING = 'PENDING_ONBOARDING',
}

export type PropertyType =
  | 'apartamento'
  | 'penthouse'
  | 'estudio'
  | 'casa'
  | 'dÃºplex'
  | 'villa'
  | 'finca'
  | 'solar'
  | 'local'
  | 'oficina'
  | 'nave_industrial'
  | 'edificio'
  | 'hotel'
  | 'resort'
  | 'marina';

export type BusinessCategory =
  | 'venta'
  | 'alquiler_largo_plazo'
  | 'alquiler_temporal'
  | 'preventa'
  | 'proyecto_en_planos'
  | 'remate'
  | 'permuta'
  | 'tiempo_compartido';

export type PropertyStatus =
  | 'available'
  | 'sold'
  | 'rented'
  | 'negotiation'
  | 'temporary'
  | 'inactive';

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Form types
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  tenant: string;
  role: UserRole;
}

export interface PropertyFormData {
  title: string;
  description: string;
  price: number;
  currency: string;
  propertyType: PropertyType;
  businessCategory: BusinessCategory;
  location: Omit<Location, 'coordinates'>;
  characteristics: PropertyCharacteristics;
  tags: string[];
}

// WebPage Module Types
export interface Webpage {
  id: string;
  title: string;
  slug: string;
  description?: string;
  content: string;
  grapejsData: GrapeJSData;
  seo: WebpageSEO;
  status: WebpageStatus;
  isPublished: boolean;
  publishedAt?: Date;
  tenantId: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface GrapeJSData {
  components: any[];
  styles: any[];
  assets: any[];
  pages: any[];
}

export interface WebpageSEO {
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string[];
  ogImage?: string;
}

export type WebpageStatus = 'draft' | 'published' | 'archived';

export interface WebpagePermissions {
  canCreate: boolean;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canPublish: boolean;
  canExport: boolean;
  canImport: boolean;
  canManageTemplates: boolean;
  canManageSettings: boolean;
  canViewAnalytics: boolean;
}
